import React, { useMemo } from "react";
import { Entity } from "@common/types/entity";
import { LineItemFallbackAccountType } from "@common/helpers/accounting";
import { currencyFormatter } from "@common/helpers/currency";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { ColumnDef } from "@components/spreadsheet/types";
import { CalculatedAmountCell, CellType } from "@components/spreadsheet/cells";
import { AccrualMode } from "./types";
import { ImporterAccountSearchCell } from "./ImporterAccountSearchCell";
import { counterpartyCellFn } from "@components/importer/counterpartyCellFn";
import styles from "./AccrualImporterDrawer.module.scss";

export function useAccrualImporterColumns({
    mode,
    counterpartiesMap,
    entities,
}: {
    mode: AccrualMode;
    counterpartiesMap: Map<string, string>;
    entities: Entity[];
}): ColumnDef[] {
    const isBill = mode === "bill";

    return useMemo(
        () => [
            {
                field: "_rowNumber",
                label: "",
                cellType: CellType.ROW_NUMBER,
                width: 40,
                pinned: "left",
                isEditable: false,
                aggregation: () => "Total",
            },
            {
                field: "entityId",
                label: "Entity",
                cellType: CellType.ENTITY,
                entities,
                width: 120,
            },
            {
                field: "createdDate",
                label: "Created date",
                cellType: CellType.DATE,
                width: 120,
            },
            {
                field: "counterpartyId",
                label: isBill ? "Vendor" : "Customer",
                cellType: CellType.COUNTERPARTY,
                counterpartiesMap,
                width: 160,
                cellFn: counterpartyCellFn(counterpartiesMap),
            },
            {
                field: "invoiceNumber",
                label: isBill ? "Bill number" : "Invoice number",
                cellType: CellType.STRING,
                width: 140,
            },
            {
                field: "memo",
                label: "Memo",
                cellType: CellType.STRING,
                width: 180,
            },
            {
                field: "productOrService",
                label: "Item",
                cellType: CellType.STRING,
                width: 150,
            },
            {
                field: "description",
                label: "Description",
                cellType: CellType.STRING,
                width: 160,
            },
            {
                field: "classIds",
                label: "Class",
                cellType: CellType.CLASS,
                width: 120,
            },
            {
                field: "quantity",
                label: "Qty",
                cellType: CellType.NUMBER,
                allowNegative: false,
                width: 90,
            },
            {
                field: "unitPrice",
                label: "Rate",
                cellType: CellType.NUMBER,
                isMonetary: true,
                width: 100,
            },
            {
                field: "amount",
                label: "Amount",
                cellType: CellType.NUMBER,
                isMonetary: true,
                width: 110,
                isEditable: false,
                className: styles.amountCell,
                cellFn: (props) =>
                    React.createElement(CalculatedAmountCell, {
                        ...props,
                        rateField: "unitPrice",
                    }),
                aggregation: (data) =>
                    currencyFormatter.format(
                        sumMonetaryValues(data.map((row) => row.amount ?? 0)),
                    ),
            },
            {
                field: "dueDate",
                label: "Due date",
                cellType: CellType.DATE,
                width: 120,
            },
            {
                field: "accrualLedger",
                label: "Accrual ledger",
                cellType: CellType.STRING,
                width: 160,
                cellFn: (props) =>
                    ImporterAccountSearchCell({
                        ...props,
                        value: props.value as string | null,
                        fallbackMode: isBill
                            ? LineItemFallbackAccountType.billExpense
                            : LineItemFallbackAccountType.invoiceRevenue,
                    }),
            },
            {
                field: "accrualAccountId",
                label: isBill ? "Payables account" : "Receivables account",
                cellType: CellType.STRING,
                width: 180,
                cellFn: (props) =>
                    ImporterAccountSearchCell({
                        ...props,
                        value: props.value as string | null,
                        fallbackMode: isBill
                            ? LineItemFallbackAccountType.billAccountsPayable
                            : LineItemFallbackAccountType.invoiceAccountsReceivable,
                    }),
            },
        ],
        [isBill, counterpartiesMap, entities],
    );
}
